import { gql, useApolloClient } from "@apollo/client"
import { useEffect, useRef, useState } from "react"
import { blocksContentOnlyQuery } from "../graphql/schema"
import { BlocksContentType } from "../types"

const cursorQuery = gql`
  query DraftPreviewCursor($contextKey: String!) {
    cursor: DraftPreviewCursor(contextKey: $contextKey)
  }
`

// Dedicated read-only host: never writes into Apollo's editor/learner cache.
export function useDraftPreview(contextKey: string, enabled: boolean) {
  const client = useApolloClient()
  const retryRef = useRef<() => void>(() => {})
  const [state, setState] = useState<{
    target: string
    content?: BlocksContentType
    revision?: string
    error?: string
    stopped?: boolean
  }>({ target: contextKey })

  useEffect(() => {
    if (!enabled) return
    let disposed = false
    let busy = false
    let stopped = false
    let cursor: string | undefined
    let failures = 0
    let timer: ReturnType<typeof setTimeout>
    setState({ target: contextKey })
    const poll = async () => {
      clearTimeout(timer)
      if (disposed || stopped || busy || document.hidden) return
      busy = true
      try {
        const result = await client.query({
          query: cursorQuery,
          variables: { contextKey },
          fetchPolicy: "no-cache"
        })
        if (disposed) return
        if (cursor !== result.data.cursor) {
          const snapshot =
            result.data.cursor === "empty"
              ? {
                  data: { content: { id: `preview-${contextKey}`, blocks: [] } }
                }
              : await client.query({
                  query: blocksContentOnlyQuery,
                  variables: {
                    contextKey,
                    mode: "EDIT",
                    draft: true,
                    upsert: false
                  },
                  fetchPolicy: "no-cache"
                })
          if (disposed) return
          cursor = result.data.cursor
          setState({
            target: contextKey,
            content: snapshot.data.content,
            revision: cursor
          })
        } else {
          setState((previous) => ({ ...previous, error: undefined }))
        }
        failures = 0
      } catch (error) {
        if (disposed) return
        const codes =
          (error as any)?.graphQLErrors?.map(
            (item: any) => item.extensions?.code
          ) || []
        const unavailable = codes.includes("PREVIEW_UNAVAILABLE")
        stopped =
          unavailable ||
          codes.includes("FORBIDDEN") ||
          codes.includes("UNAUTHENTICATED")
        failures++
        setState((previous) =>
          stopped
            ? {
                target: contextKey,
                stopped: true,
                error: unavailable
                  ? "Live draft preview is not available for this Step yet."
                  : "Draft preview access ended. Reload after signing in."
              }
            : { ...previous, error: "Preview could not refresh. Retrying…" }
        )
      } finally {
        busy = false
        if (!disposed && !stopped)
          timer = setTimeout(poll, Math.min(3000 * 2 ** failures, 30000))
      }
    }
    retryRef.current = poll
    void poll()
    document.addEventListener("visibilitychange", poll)
    window.addEventListener("focus", poll)
    window.addEventListener("online", poll)
    return () => {
      disposed = true
      retryRef.current = () => {}
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", poll)
      window.removeEventListener("focus", poll)
      window.removeEventListener("online", poll)
    }
  }, [client, contextKey, enabled])
  return enabled && state.target === contextKey
    ? { ...state, retry: () => retryRef.current() }
    : undefined
}
